import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Skeleton } from "@mui/material";
import styled from "styled-components";
import { useEffect, useRef, useState } from "react";
import { useSettingContext } from "context/SettingContext";
import RemoveModal from "components/ui/RemoveModal/RemoveModal";
import { getIcon } from "utils/iconsConfig";
import ClientModal from "components/ui/clientModal/clientModal";
import { useLocation, useNavigate } from "react-router-dom";
import { useGlobalContext } from "context/GlobalContext";
import { useTrays } from "./Api/stockTrayApi";
import apiClient from "lib/api-client";
import { formatTrayName } from "utils/capitalEachLetter";
import { usePermissions } from "context/PermissionsContext";
import { findSpecificMenu, findSpecificSubMenu, isAllowed } from "utils/checkUserRigths";

const StyledTableContainer = styled(TableContainer)(({}) => ({
  backgroundColor: "transparent",
  maxHeight: "60.5vh",
  // height: "70vh",
  overflowX: "auto",
  background: "#000",
  position: "relative",
  border: "1px solid #fff",
  scrollbarWidth: "none", // Firefox
  "&::-webkit-scrollbar": {
    height: "6px",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#888",
    borderRadius: "3px",
  },

  "@media (max-width: 768px)": {
    maxWidth: "100%",
    overflowX: "scroll",
  },
}));

const StyledTable = styled(Table)(({}) => ({
  "& thead": {
    backgroundColor: "#444444",
    position: "sticky",
    top: 0,
    zIndex: 1,
    "& th": {
      fontSize: "14px",
      fontWeight: 400,
      lineHeight: "22px",
      color: "#FFFFFF",
      textAlign: "center",
      fontFamily: "Poppins",
      padding: "10px",
      borderBottom: "1px solid #fff",
    },
  },
  "& tbody": {
    "& td": {
      fontSize: "16px",
      fontWeight: 400,
      lineHeight: "22px",
      color: "#FFFFFF",
      textAlign: "center",
      fontFamily: "Poppins",
      borderBottom: "1px solid #fff",
      padding: "10px 5px",
      border: "1px solid #fff",
    },
  },
  "& th": {
    // backgroundColor: "#444444",
    height: "50px",
    // position: "sticky",
    // top: 0,
    // zIndex: 1,
  },
}));

const StyledTableHeaderCell = styled(TableCell)`
  padding: 15px;
  font-size: 14px !important;
  font-weight: 400 !important;
  background-color: #444444;
  color: white;
  text-align: center !important;
  position: sticky;
  top: 0;
  z-index: 10;
  border: 1px solid #fff;

  &.narrow {
    width: 20%;
  }

  &.default {
    width: 40%;
  }
`;

const StyledTableRow = styled(TableRow)`
  border-bottom: 1px solid #fff;
  & td {
    padding: 5px;
  }
`;

const StyledTableBodyCell = styled(TableCell)`
  padding: 10px;
  text-align: center;

   &.narrow {
    width: 20%; 

  &.default {
    width: 40%;
  }
`;

const buttonStyle = (color) => ({
  backgroundColor: "transparent",
  color,
  padding: "3px 12px",
  border: `1px solid ${color}`,
  borderRadius: "6px",
  fontWeight: 500,
  fontFamily: "Poppins",
  fontSize: "14px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: "5px",
});

function debounce(fn, delay) {
  let timer = null;
  return (...args) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

const StockTray = () => {
  const { setCreateBtn, setCreateBtnName, addClient, setAddClient, search, setSearch, setSearchBar, editTrayData, setEditTrayData } = useSettingContext();
  const { setSearchPlaceHolder } = useGlobalContext();
  const [dustbinModalOpen, setDustbinModalOpen] = useState(false);
  const [selectedTrayName, setSelectedTrayName] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const tableRef = useRef(null);

  useEffect(() => {
    setSearchBar(true);
    setSearchPlaceHolder("Search By Tray Name");

    return () => {
      setSearchBar(false);
      setSearch("");
      setSearchPlaceHolder("");
    };
  }, []);


  const permission=usePermissions();
const hasAddTrayAccess = isAllowed(findSpecificSubMenu(findSpecificMenu("Vision Web", permission), "Stock Tray"), "add");
const hasEditAccess = isAllowed(findSpecificSubMenu(findSpecificMenu("Vision Web", permission), "Stock Tray"), "edit");
const hasDeleteAccess = isAllowed(findSpecificSubMenu(findSpecificMenu("Vision Web", permission), "Stock Tray"), "delete");



  // ----------------------------------------------------  GET ALL TRAY  --------------------------------------------------------------------------

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage, isError, refetch } = useTrays({
    limit: 15,
    search,
  });

  const allTrayData = data?.pages?.flatMap((page) => page.trayData) || [];

  const handleScroll = debounce(() => {
    const element = tableRef.current;
    if (!element || isFetchingNextPage || !hasNextPage) return;

    const { scrollTop, scrollHeight, clientHeight } = element;
    const reachedBottom = scrollTop + clientHeight >= scrollHeight - 10;

    if (reachedBottom) {
      fetchNextPage();
    }
  }, 300);
  
  useEffect(() => {
      if (hasAddTrayAccess) {
    setCreateBtn(true);
    setCreateBtnName("Add Tray +");
      }
    return () => {
      setCreateBtn(false);
      setCreateBtnName("");
    };
  }, [hasAddTrayAccess]);

  const discardChanges = () => {
    setDustbinModalOpen(false);
  };

  const handleDelete = (rowData) => {
    setSelectedTrayName(rowData);
    setDustbinModalOpen(true);
  };

  // ----------------------------------------------------  DELETE TRAY  --------------------------------------------------------------------------

  const handleConfirmDelete = async () => {
    try {
      const response = await apiClient.put(`/api/v1/tray/deleteTray/${selectedTrayName?._id}`);

      if (response.status === 200) {
        discardChanges();
        refetch?.();
      } else {
        console.error(response.data?.message || "Failed to delete tray.");
      }
    } catch (error) {
      console.error("Error deleting tray:", error);
      console.error(error.response?.data?.message || "Something went wrong.");
    }
  };

  const btnInfo = [
    {
      btnName: "No",
      bgColor: "#E77B7B",
      click: () => {
        discardChanges();
      },
    },
    {
      btnName: "Yes",
      bgColor: "#50c878",
      click: handleConfirmDelete,
    },
  ];

  const TableSkeleton = ({ columns, rows = 5 }) => (
    <>
      {Array.from(new Array(rows)).map((_, rowIndex) => (
        <StyledTableRow key={`skeleton-${rowIndex}`}>
          {Array.from(new Array(columns)).map((_, cellIndex) => (
            <StyledTableBodyCell key={`skeleton-cell-${cellIndex}`}>
              <Skeleton variant="text" sx={{ fontSize: "1rem" }} animation="wave" width={"100%"} height={"90%"} />
            </StyledTableBodyCell>
          ))}
        </StyledTableRow>
      ))}
    </>
  );

  return (
    <>
      <div style={{ marginTop: "15px", marginLeft: "6px", marginRight: "11.5px", backgroundColor: "#000", padding: "10px" }}>
        <StyledTableContainer ref={tableRef} onScroll={handleScroll}>
          {/* <StyledTableContainer> */}
          <StyledTable>
            <TableHead>
              <TableRow>
                <StyledTableHeaderCell className="narrow">Sr no.</StyledTableHeaderCell>
                <StyledTableHeaderCell className="default">Tray</StyledTableHeaderCell>
                <StyledTableHeaderCell className="default">Action</StyledTableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableSkeleton columns={3} rows={10} />
              ) : allTrayData?.length === 0 ? (
                <StyledTableRow>
                  <StyledTableBodyCell colSpan={6} style={{ textAlign: "center", padding: "20px", color: "#fff" }}>
                    No Data Available
                  </StyledTableBodyCell>
                </StyledTableRow>
              ) : (
                allTrayData.map((item, index) => (
                  <StyledTableRow key={item?._id}>
                    <StyledTableBodyCell className="narrow">{index + 1}.</StyledTableBodyCell>
                    <StyledTableBodyCell className="default">{formatTrayName(item?.trayName)}</StyledTableBodyCell>
                    <StyledTableBodyCell className="default">
                      {!hasEditAccess && !hasDeleteAccess ? (
                         "No actions available"
                        ) : (
                      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "10px" }}>
                        {hasEditAccess && (
                        <button
                          onClick={() => {
                            setEditTrayData(item);
                            setAddClient(true);
                          }}
                          style={buttonStyle("#50C878")}
                        >
                          {getIcon("greenEditIcon")} Edit
                        </button>
                        )}

                        {hasDeleteAccess && (
                        <button onClick={() => handleDelete(item)} style={buttonStyle("#E77B7B")}>
                          {getIcon("DustbinIcon")} Delete
                        </button>
                        )}
                      </div>
  )}
                    </StyledTableBodyCell>
                  </StyledTableRow>
                ))
              )}
              {isFetchingNextPage && <TableSkeleton columns={3} rows={5} />}
            </TableBody>
          </StyledTable>
        </StyledTableContainer>
      </div>
      <RemoveModal open={dustbinModalOpen} setOpen={setDustbinModalOpen} btnInfo={btnInfo} heading={`Are you sure you want to delete ${formatTrayName(selectedTrayName?.trayName)}?`} oneLiner={true} />
      <ClientModal
        open={addClient}
        onClose={() => {
          setAddClient(false);
          setEditTrayData(null);
        }}
        type="addTray"
        editTrayData={editTrayData}
        onSuccess={refetch}
      />
    </>
  );
};

export default StockTray;
